import React from 'react';
import './WeatherTerrain.css';

const WeatherTerrain = () => {
  return (
    <div className="weather-terrain">
      <div className="weather-terrain__header">
        <h1>Weather &amp; Terrain Effects Guide</h1>
        <p>Understanding weather and terrain effects is crucial for competitive battling. These field conditions can dramatically change the flow of battle.</p>
        <p className="weather-terrain__format-note">
          Setters listed are eligible in <strong>Pokémon Champions Regulation M-A</strong> (base Paldea dex, no legendaries/paradox/DLC).
        </p>
      </div>

      <div className="weather-terrain__content">
        <section className="weather-terrain__section">
          <h2>Weather Effects</h2>

          <div className="weather-terrain__grid">
            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Sun / Harsh Sunlight</h3>
              </div>
              <ul>
                <li>Boosts Fire-type moves by 50%</li>
                <li>Weakens Water-type moves by 50%</li>
                <li>Solar Beam / Solar Blade require no charge turn</li>
                <li>Activates Chlorophyll (doubles Speed of Grass-types)</li>
                <li>Duration: 5 turns (8 with Heat Rock)</li>
                <li><strong>Reg M-A setters:</strong> Torkoal (Drought), Ninetales (Drought)</li>
                <li><strong>Notable synergy:</strong> Mega Meganium's Mega Sol ability — permanent Sun effect without needing a setter</li>
              </ul>
            </div>

            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Rain</h3>
              </div>
              <ul>
                <li>Boosts Water-type moves by 50%</li>
                <li>Weakens Fire-type moves by 50%</li>
                <li>Thunder has 100% accuracy</li>
                <li>Activates Swift Swim (doubles Speed of Water-types)</li>
                <li>Duration: 5 turns (8 with Damp Rock)</li>
                <li><strong>Reg M-A setters:</strong> Pelipper (Drizzle), Politoed (Drizzle)</li>
              </ul>
            </div>

            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Sandstorm</h3>
              </div>
              <ul>
                <li>Rock-types gain 50% Special Defense</li>
                <li>Damages non-Rock / Ground / Steel types each turn</li>
                <li>Activates Sand Rush (doubles Speed) and Sand Force</li>
                <li>Duration: 5 turns (8 with Smooth Rock)</li>
                <li><strong>Reg M-A setters:</strong> Hippowdon (Sand Stream), Tyranitar (Sand Stream)</li>
                <li><strong>Notable synergy:</strong> Mega Excadrill — Sand Rush doubles Speed in sand</li>
              </ul>
            </div>

            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Snow</h3>
              </div>
              <ul>
                <li>Ice-types gain 50% Defense (replaces old Hail chip damage)</li>
                <li>Activates Slush Rush (doubles Speed of Ice-types)</li>
                <li>Duration: 5 turns (8 with Icy Rock)</li>
                <li><strong>Reg M-A setters:</strong> Abomasnow (Snow Warning)</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="weather-terrain__section">
          <h2>Terrain Effects</h2>

          <div className="weather-terrain__grid">
            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Electric Terrain</h3>
              </div>
              <ul>
                <li>Boosts Electric-type moves by 30% for grounded Pokémon</li>
                <li>Prevents sleep on grounded Pokémon</li>
                <li>Duration: 5 turns (8 with Terrain Extender)</li>
                <li><strong>Reg M-A setters:</strong> Pincurchin (Electric Surge)</li>
              </ul>
            </div>

            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Grassy Terrain</h3>
              </div>
              <ul>
                <li>Boosts Grass-type moves by 30% for grounded Pokémon</li>
                <li>Heals grounded Pokémon by 1/16 HP per turn</li>
                <li>Weakens Earthquake, Magnitude, and Bulldoze by 50%</li>
                <li>Duration: 5 turns (8 with Terrain Extender)</li>
                <li><strong>Reg M-A setters:</strong> Move-based (Grassy Terrain move)</li>
              </ul>
            </div>

            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Misty Terrain</h3>
              </div>
              <ul>
                <li>Halves damage from Dragon-type moves to grounded Pokémon</li>
                <li>Prevents status conditions on grounded Pokémon</li>
                <li>Duration: 5 turns (8 with Terrain Extender)</li>
                <li><strong>Reg M-A setters:</strong> Move-based (Misty Terrain move)</li>
              </ul>
            </div>

            <div className="weather-terrain__card">
              <div className="weather-terrain__card-header">
                <h3>Psychic Terrain</h3>
              </div>
              <ul>
                <li>Boosts Psychic-type moves by 30% for grounded Pokémon</li>
                <li>Blocks priority moves targeting grounded Pokémon</li>
                <li>Duration: 5 turns (8 with Terrain Extender)</li>
                <li><strong>Reg M-A setters:</strong> Indeedee (Psychic Surge)</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="weather-terrain__section">
          <h2>Strategic Tips</h2>
          <div className="weather-terrain__tips">
            <ul>
              <li>Weather and terrain effects don't stack — the newest one overrides the previous</li>
              <li>Consider team synergy when building around weather/terrain setters</li>
              <li>Watch out for abilities that change or prevent weather (Air Lock, Cloud Nine)</li>
              <li>Use held items to extend duration of beneficial conditions (Heat Rock, Damp Rock, Terrain Extender)</li>
              <li>Some abilities become stronger in specific weather (Swift Swim in rain, Sand Rush in sandstorm)</li>
              <li><strong>Reg M-A note:</strong> Mega Meganium's Mega Sol provides a permanent Sun effect — no setter needed, and it can't be overridden by a turn-limited weather move</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WeatherTerrain;
